import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Scissors, Play, Flame, Loader2, ArrowRight, Film, Mic2, Link as LinkIcon, Check, Search, ExternalLink } from "lucide-react";
import { getDemo, mediaUrl, shareUrl, publicSearch } from "../lib/api";

function CopyRow({ label, url, testid }) {
  const [c, setC] = useState(false);
  return (
    <div className="flex items-center justify-between gap-3 border border-border bg-card px-4 py-3">
      <div className="min-w-0">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
        <p className="truncate font-body text-sm text-white">{url}</p>
      </div>
      <div className="flex shrink-0 gap-2">
        <a href={url} target="_blank" rel="noreferrer" className="grid h-9 w-9 place-items-center border border-border text-accent hover:border-accent" title="Open">
          <ExternalLink className="h-4 w-4" />
        </a>
        <button data-testid={testid}
                onClick={() => { navigator.clipboard.writeText(url); setC(true); setTimeout(() => setC(false), 1600); }}
                className="grid h-9 w-9 place-items-center border border-accent text-accent hover:bg-accent hover:text-black" title="Copy">
          {c ? <Check className="h-4 w-4" /> : <LinkIcon className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

function TryItPanel({ handle, featuredId, examples }) {
  const origin = window.location.origin;
  const [tq, setTq] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = (query) => {
    const query2 = (query ?? tq).trim();
    if (!query2 || !handle) return;
    setTq(query2); setBusy(true); setRes(null);
    publicSearch(handle, { q: query2 }).then(setRes).catch(() => setRes({ counts: {} })).finally(() => setBusy(false));
  };
  if (!handle) return null;
  return (
    <section className="mx-auto max-w-6xl px-6 py-10" data-testid="try-it-panel">
      <div className="border border-accent/30 bg-accent/[0.04] p-6 sm:p-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent">See it work live</p>
        <h2 className="mt-2 font-display text-3xl tracking-wide">Type a topic. Watch the archive answer.</h2>
        <p className="mt-2 max-w-2xl font-body text-sm text-muted-foreground">
          This is a real creator page powered by Clip Miner. Search any subject and it returns the exact
          clips, prayer &amp; care moments, chapters and resources across every episode.
        </p>

        <div className="mt-5 flex max-w-2xl gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="try-search-input" value={tq} onChange={(e) => setTq(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && run()}
                   placeholder="Try “domestic violence”"
                   className="w-full rounded-none border border-border bg-card py-3 pl-10 pr-3 font-body text-sm text-white outline-none focus:border-accent" />
          </div>
          <button data-testid="try-search-btn" onClick={() => run()} disabled={busy}
                  className="bg-accent px-6 font-body text-sm font-semibold text-black disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
          </button>
        </div>

        <div className="mt-3 flex flex-wrap gap-2" data-testid="try-examples">
          {(examples || []).map((ex) => (
            <button key={ex} data-testid={`try-ex-${ex.replace(/\s+/g, "-")}`} onClick={() => run(ex)}
                    className="border border-border px-3 py-1.5 font-mono text-xs text-muted-foreground transition-colors hover:border-accent hover:text-accent">
              {ex}
            </button>
          ))}
        </div>

        {res && (
          <div className="mt-6" data-testid="try-results">
            <div className="flex flex-wrap gap-3">
              <Pill n={res.counts?.clips || 0} label="Clips" />
              <Pill n={res.counts?.care || 0} label="Prayer & care" />
              <Pill n={res.counts?.chapters || 0} label="Chapters" />
              <Pill n={res.counts?.resources || 0} label="Resources" />
            </div>
            {(res.care?.length > 0) && (
              <div className="mt-4 space-y-1.5">
                {res.care.slice(0, 3).map((e, i) => (
                  <p key={i} className="font-body text-xs text-muted-foreground">
                    <span className="text-white">{e.person || "Anonymous"}</span>
                    {e.topic && <span className="ml-2 text-primary">· {e.topic}</span>} — {e.summary}
                  </p>
                ))}
              </div>
            )}
            <a href={`${origin}/u/${handle}${tq ? `` : ``}`} target="_blank" rel="noreferrer"
               data-testid="try-open-full"
               className="mt-5 inline-flex items-center gap-2 border border-accent px-5 py-2.5 font-body text-sm font-semibold text-accent transition-colors hover:bg-accent hover:text-black">
              Open the full creator page <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        )}

        <div className="mt-8 grid gap-3 sm:grid-cols-2" data-testid="try-links">
          <CopyRow label="Creator page (share this)" url={`${origin}/u/${handle}`} testid="copy-creator-link" />
          {featuredId && <CopyRow label="Flagship full episode" url={`${origin}/s/${featuredId}`} testid="copy-episode-link" />}
        </div>
      </div>
    </section>
  );
}

const Pill = ({ n, label }) => (
  <div className="flex items-baseline gap-2 border border-border bg-card px-4 py-2">
    <span className="font-display text-2xl text-accent">{n}</span>
    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
  </div>
);

export default function Demo() {
  const navigate = useNavigate();
  const [data, setData] = useState({ shows: [], clips: [] });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    getDemo().then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const login = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background text-white" data-testid="demo-page">
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center bg-accent"><Scissors className="h-4 w-4 text-black" /></div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-accent">Clip Miner · Demo</span>
          </div>
          <button data-testid="demo-cta-top" onClick={login}
                  className="flex items-center gap-2 bg-accent px-4 py-2 font-body text-xs font-semibold text-black transition-transform hover:-translate-y-0.5">
            Try it free <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-6 pt-16">
        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                   className="font-display text-5xl leading-[1.05] tracking-tight sm:text-6xl">
          Real shows.<br /><span className="text-accent">Real clips.</span> Auto-mined.
        </motion.h1>
        <p className="mt-5 max-w-xl font-body text-base text-muted-foreground">
          Every clip below was pulled from a full-length set by Clip Miner — transcribed, scored for
          laughs, reframed vertical and captioned. This is a live look at a real workspace.
        </p>
        {!loading && (
          <div className="mt-8 flex flex-wrap gap-8">
            <Stat n={data.shows.length} label="Shows mined" icon={Mic2} />
            <Stat n={data.clips.length} label="Top clips" icon={Film} />
            <Stat n={data.clips[0]?.funny_score || 0} label="Top laugh score" icon={Flame} />
          </div>
        )}
      </section>

      {!loading && data.handle && (
        <TryItPanel handle={data.handle} featuredId={data.featured_show_id} examples={data.example_queries} />
      )}

      {loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>
      ) : (
        <section className="mx-auto max-w-6xl px-6 py-14">
          <h2 className="font-display text-2xl tracking-wide">The best of the bunch</h2>
          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {data.clips.map((c, i) => (
              <motion.div
                key={c.id}
                data-testid={`demo-clip-${c.id}`}
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.02, 0.4) }}
                className="group relative aspect-[9/16] cursor-pointer overflow-hidden border border-border bg-card"
                onClick={() => setActive(c)}
              >
                {(c.poster || c.reframe_thumb) ? (
                  <img src={mediaUrl(c.poster || c.reframe_thumb)} alt={c.title} loading="lazy" className="h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <div className="grid h-full w-full place-items-center bg-secondary text-muted-foreground"><Film className="h-8 w-8" /></div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                <div className="absolute right-2 top-2 flex items-center gap-1 bg-accent px-1.5 py-0.5 font-mono text-[10px] font-bold text-black">
                  <Flame className="h-3 w-3" /> {c.funny_score}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="font-display text-sm leading-tight text-white">{c.title}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/60">{c.show?.title}</p>
                </div>
                <div className="absolute inset-0 grid place-items-center opacity-0 transition-opacity group-hover:opacity-100">
                  <div className="grid h-12 w-12 place-items-center rounded-full bg-accent text-black"><Play className="h-5 w-5" /></div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 border border-accent/30 bg-accent/[0.05] p-8 text-center">
            <h3 className="font-display text-3xl tracking-wide">Turn your sets into a feed like this</h3>
            <p className="mx-auto mt-3 max-w-lg font-body text-sm text-muted-foreground">
              Drop a full show, get back scroll-stopping clips in your comedic voice — free to start.
            </p>
            <button data-testid="demo-cta-bottom" onClick={login}
                    className="mt-6 inline-flex items-center gap-2 bg-accent px-6 py-3 font-body text-sm font-semibold text-black transition-transform hover:-translate-y-0.5">
              Start free <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </section>
      )}

      {active && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/85 p-4" data-testid="demo-player" onClick={() => setActive(null)}>
          <div className="w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <video src={mediaUrl(active.video_path)} controls autoPlay playsInline
                   className="aspect-[9/16] w-full bg-black" />
            <div className="mt-3">
              <p className="font-display text-lg text-white">{active.title}</p>
              <p className="mt-1 font-body text-sm text-muted-foreground">{active.caption}</p>
              <p className="mt-2 font-mono text-xs text-accent">{(active.hashtags || []).join(" ")}</p>
              <button
                data-testid="demo-copy-link"
                onClick={() => { navigator.clipboard.writeText(shareUrl(active.id)); setCopied(true); setTimeout(() => setCopied(false), 1800); }}
                className="mt-3 flex items-center gap-2 border border-accent px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-widest text-accent transition-colors hover:bg-accent hover:text-black"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <LinkIcon className="h-3.5 w-3.5" />}
                {copied ? "Link copied" : "Copy share link"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ n, label, icon: Icon }) => (
  <div className="flex items-center gap-3">
    <div className="grid h-10 w-10 place-items-center border border-border text-accent"><Icon className="h-5 w-5" /></div>
    <div>
      <p className="font-display text-2xl text-white">{n}</p>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
    </div>
  </div>
);
